import { useIsBillingAdmin, useProfessionalCreditStatus } from '@common/credits/ProfessionalEdition/hooks';
import { useReplayModeType } from '@common/Replay/utils';
import { Dialog, Stack, theme } from '@prophecy/ui';
import { AlertTriangleIcon } from '@prophecy/ui-v3/Icons';
import { Text } from '@prophecy/ui-v3/Typography';
import { useLocationPathName } from '@prophecy/utils/history';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { USAGE_AND_BILLING_PAGE } from '../constants';
import { getUsageAlertFromStorage, setUsageAlertToStorage } from './data/localStorage';
import { useProfessionalEditionCreditsContext } from './useProfessionalEditionCreditsContext';
import { isUsageAndBillingPage } from './utils';

export const UsageAlertDialog = () => {
  const {
    usageLimit: { alertAmount },
    isAtAlertLimit,
    usageAlertCredits
  } = useProfessionalCreditStatus();

  const navigate = useNavigate();
  const isBillingAdmin = useIsBillingAdmin();
  const { meteringInfo } = useProfessionalEditionCreditsContext();
  const { teamID, userID } = meteringInfo;

  const [isOpen, setIsOpen] = useState(false);

  const { hasReplayMode } = useReplayModeType();

  useEffect(() => {
    if (!alertAmount || !isAtAlertLimit || hasReplayMode) return;

    const lastShownUsageAlertLimit = getUsageAlertFromStorage(teamID, userID);

    // Skip if the last shown usage alert limit is the same as the current alert amount
    if (lastShownUsageAlertLimit === alertAmount) {
      return;
    }
    setIsOpen(true);
    setUsageAlertToStorage(teamID, userID, alertAmount);
  }, [alertAmount, hasReplayMode, isAtAlertLimit, teamID, userID]);

  const pathname = useLocationPathName();
  const isOnUsageAndBillingPage = isUsageAndBillingPage(pathname);

  if (!isAtAlertLimit || hasReplayMode) return null;

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleIncreaseUsageAlertLimit = () => {
    if (!isBillingAdmin) return;
    setIsOpen(false);
    if (isOnUsageAndBillingPage) {
      return;
    }
    navigate(USAGE_AND_BILLING_PAGE);
  };

  const okButton =
    isBillingAdmin && !isOnUsageAndBillingPage
      ? { children: 'Manage Monthly Spending', onClick: handleIncreaseUsageAlertLimit }
      : null;
  return (
    <Dialog
      open={isOpen}
      data-test-id='usage-alert-dialog'
      title={
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x6}>
          <AlertTriangleIcon size='m' color={theme.colors.warning500} type='default' />
          <Text level='lg' tone={theme.colors.gray900}>
            Usage Alert
          </Text>
        </Stack>
      }
      isBlocking={true}
      size='l'
      closeButton={{ children: 'Close', onClick: handleClose }}
      okButton={okButton}
      onOk={handleIncreaseUsageAlertLimit}>
      <Stack direction='vertical' gap={theme.spaces.x12} padding={`${theme.spaces.x12} 0`}>
        <Text level='sm15' tone={theme.colors.gray600}>
          Your team has consumed <strong>{usageAlertCredits}</strong> credits reaching the monthly threshold of{' '}
          <strong>${alertAmount}</strong>.
        </Text>
        <Text level='sm15' tone={theme.colors.gray600}>
          {isBillingAdmin
            ? 'Services will continue to operate normally. You can review or update the settings.'
            : 'Services will continue to operate normally. Please contact the team admin to review or update the settings.'}
        </Text>
      </Stack>
    </Dialog>
  );
};
